import { lazy, Suspense, type ReactNode } from 'react';
import { Link, Navigate, type RouteObject } from 'react-router';
import { AuthLayout } from '../layouts/AuthLayout';
import { TenantLayout } from '../layouts/TenantLayout';
import { LoginPage } from '../features/auth/LoginPage';
import { SignUpPage } from '../features/auth/SignUpPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { AuthActionPage } from '../features/auth/AuthActionPage';
import { AwaitingApprovalPage, DeactivatedPage } from '../features/auth/StatusPages';
import { TenantDashboard } from '../features/tenant/TenantDashboard';
import { RaiseTicketPage } from '../features/tickets/RaiseTicketPage';
import { CountdownPage } from '../features/site/CountdownPage';
import { MaintenancePage } from '../features/site/MaintenancePage';
import { SiteGate } from './SiteGate';
import { AUTH_ACTION_PATH, GATE_PATHS } from './siteModeContext';
import {
  FullPageLoader,
  PublicOnly,
  RequireAuth,
  RequireRole,
  RequireStatus,
  StatusGate,
} from './guards';
import { t } from '../lib/copy';

// Heavier, role-specific screens load on demand.
const TicketChatPage = lazy(() =>
  import('../features/tickets/TicketChatPage').then((m) => ({ default: m.TicketChatPage })),
);
const GeneralChatPage = lazy(() =>
  import('../features/chat/GeneralChatPage').then((m) => ({ default: m.GeneralChatPage })),
);
const AdminLayout = lazy(() =>
  import('../layouts/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminDashboard = lazy(() =>
  import('../features/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const UnitsPage = lazy(() =>
  import('../features/units/UnitsPage').then((m) => ({ default: m.UnitsPage })),
);
const UnitDetailPage = lazy(() =>
  import('../features/units/UnitDetailPage').then((m) => ({ default: m.UnitDetailPage })),
);
const ApprovalsPage = lazy(() =>
  import('../features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
);
const TenantDetailPage = lazy(() =>
  import('../features/admin/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage })),
);
const AdminTicketsPage = lazy(() =>
  import('../features/tickets/AdminTicketsPage').then((m) => ({ default: m.AdminTicketsPage })),
);
const AdminChatInbox = lazy(() =>
  import('../features/chat/AdminChatInbox').then((m) => ({ default: m.AdminChatInbox })),
);

const s = (node: ReactNode) => <Suspense fallback={<FullPageLoader />}>{node}</Suspense>;

function NotFound() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-4 text-center">
      <h1 className="text-2xl font-bold">{t('error.notFound')}</h1>
      <Link to="/" className="font-medium text-brand hover:underline">
        Go to the home page
      </Link>
    </div>
  );
}

const appRoutes: RouteObject[] = [
  { path: '/', element: <Navigate to="/login" replace /> },
  {
    element: (
      <PublicOnly>
        <AuthLayout />
      </PublicOnly>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignUpPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  // Links in auth emails (D34). Works signed in or out, so it sits outside PublicOnly.
  {
    path: AUTH_ACTION_PATH,
    element: (
      <AuthLayout>
        <AuthActionPage />
      </AuthLayout>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        path: '/awaiting-approval',
        element: (
          <RequireStatus status="pending">
            <AwaitingApprovalPage />
          </RequireStatus>
        ),
      },
      {
        path: '/deactivated',
        element: (
          <RequireStatus status="deactivated">
            <DeactivatedPage />
          </RequireStatus>
        ),
      },
      {
        element: <StatusGate />,
        children: [
          {
            path: '/app',
            element: (
              <RequireRole allow="tenant">
                <TenantLayout />
              </RequireRole>
            ),
            children: [
              { index: true, element: <TenantDashboard /> },
              { path: 'tickets/new', element: <RaiseTicketPage /> },
              { path: 'tickets/:ticketId', element: s(<TicketChatPage />) },
              { path: 'chat', element: s(<GeneralChatPage />) },
            ],
          },
          {
            path: '/admin',
            element: <RequireRole allow="admin">{s(<AdminLayout />)}</RequireRole>,
            children: [
              { index: true, element: s(<AdminDashboard />) },
              { path: 'units', element: s(<UnitsPage />) },
              { path: 'units/:unitId', element: s(<UnitDetailPage />) },
              { path: 'approvals', element: s(<ApprovalsPage />) },
              { path: 'tenants/:uid', element: s(<TenantDetailPage />) },
              { path: 'tickets', element: s(<AdminTicketsPage />) },
              { path: 'tickets/:ticketId', element: s(<TicketChatPage />) },
              { path: 'chats', element: s(<AdminChatInbox />) },
              { path: 'chats/:uid', element: s(<AdminChatInbox />) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];

// Every path, the 404 included, sits behind the site mode gate (D32). Only the auth email
// handler passes through while gated (D34).
export const routes: RouteObject[] = [
  {
    element: <SiteGate />,
    children: [
      { path: GATE_PATHS.countdown, element: <CountdownPage /> },
      { path: GATE_PATHS.maintenance, element: <MaintenancePage /> },
      ...appRoutes,
    ],
  },
];
